/**
 * Tarjeta de presentación de un {@link Insight} del Motor_Insights.
 *
 * Renderiza, en la estética del SPA, las piezas exigidas por el diseño
 * (R5.2, R5.3, R5.7): la Narrativa_Operacional (hallazgo + acciones
 * recomendadas), el nivel de confianza, la evidencia de soporte, la
 * Cadena_Causal y los enlaces a las entidades del Grafo_Entidades y a los
 * Casos a los que aplica.
 *
 * Es un componente de presentación puro: recibe el Insight ya producido
 * por el motor (vía el runtime selector) y no consulta datos por su
 * cuenta. Lo comparten las vistas Insights, Observability y ExecutiveCenter.
 */
import { Link } from "react-router-dom";

import type { EntidadRef, Insight } from "../api/contracts";
import { Icon } from "./Icon";

/** Color del borde de la tarjeta según el nivel de confianza del Insight. */
function confianzaTone(confianza: number): string {
  if (confianza >= 0.75) return "var(--c-danger)";
  if (confianza >= 0.5) return "var(--c-warn)";
  if (confianza >= 0.25) return "var(--c-info)";
  return "var(--c-border-strong)";
}

/** Ruta de drill-down para una entidad referenciada, según su tipo. */
function entidadHref(ref: EntidadRef): string {
  switch (ref.tipo) {
    case "DMA":
      return `/mapa?dma=${ref.id}`;
    case "Caso":
      return "/casos";
    case "Cuadrilla":
      return "/cuadrillas";
    case "Gateway":
      return "/observabilidad";
    case "Anomalia":
    case "Alerta":
      return "/alertas";
    default:
      return "/mapa";
  }
}

export interface InsightCardProps {
  insight: Insight;
  /** Modo compacto para franjas laterales (Observability/ExecutiveCenter). */
  compact?: boolean;
}

/** Tarjeta completa de un Insight operacional. */
export function InsightCard({ insight, compact = false }: InsightCardProps): JSX.Element {
  const { narrativa, confianza, evidencia, cadena_causal, entidades, casos } = insight;
  const confianzaPct = Math.round(confianza * 100);

  return (
    <section
      className="card"
      style={{ borderLeft: `3px solid ${confianzaTone(confianza)}` }}
      data-insight-id={insight.insight_id}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "1fr" : "1fr auto",
          gap: 16,
          alignItems: "start",
        }}
      >
        <div>
          <div className="hstack" style={{ gap: 8, marginBottom: 6 }}>
            <span className="badge badge--accent">Insight</span>
            <span className="badge badge--neutral">
              {narrativa.entidad_afectada.tipo}: {narrativa.entidad_afectada.id}
            </span>
          </div>

          {/* Narrativa_Operacional — hallazgo (R5.3) */}
          <p style={{ color: "var(--c-text)", fontSize: 13, marginBottom: 10 }}>
            {narrativa.hallazgo}
          </p>

          {/* Acciones recomendadas (R5.3) */}
          {narrativa.acciones_recomendadas.length > 0 && (
            <div
              className="status-block status-block--info"
              style={{ flexDirection: "column", alignItems: "flex-start", gap: 4, marginBottom: 10 }}
            >
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Acciones recomendadas
              </strong>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {narrativa.acciones_recomendadas.map((a, i) => (
                  <li key={i} style={{ color: "var(--c-text)", fontSize: 12.5, marginBottom: 2 }}>
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Cadena_Causal (R5.2) */}
          {cadena_causal.vinculos.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div className="text-faint" style={{ fontSize: 10.5, textTransform: "uppercase", marginBottom: 4 }}>
                Cadena causal
              </div>
              {cadena_causal.vinculos.map((v, i) => (
                <div
                  key={i}
                  className="hstack"
                  style={{ gap: 6, fontSize: 11.5, color: "var(--c-text-muted)", marginBottom: 2 }}
                >
                  <span className="text-mono">{v.desde.tipo}:{v.desde.id}</span>
                  <Icon name="chevron-right" size={11} />
                  <span className="text-mono">{v.hacia.tipo}:{v.hacia.id}</span>
                  <span className="text-faint">· {v.relacion}</span>
                </div>
              ))}
            </div>
          )}

          {/* Evidencia de soporte (R5.2) */}
          {evidencia.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div className="text-faint" style={{ fontSize: 10.5, textTransform: "uppercase", marginBottom: 4 }}>
                Evidencia ({evidencia.length})
              </div>
              <div className="hstack" style={{ flexWrap: "wrap", gap: 6 }}>
                {evidencia.map((e, i) => (
                  <span key={i} className="badge badge--neutral text-mono" style={{ fontSize: 10.5 }}>
                    {e.tipo}: {e.id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Enlaces a entidades del Grafo y Casos (R5.7) */}
          {(entidades.length > 0 || casos.length > 0) && (
            <div className="hstack" style={{ flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {entidades.map((e, i) => (
                <Link key={`e-${i}`} to={entidadHref(e)} className="btn btn--ghost">
                  {e.tipo}: {e.id} <Icon name="chevron-right" size={11} />
                </Link>
              ))}
              {casos.map((c, i) => (
                <Link key={`c-${i}`} to="/casos" className="btn btn--ghost">
                  Caso: {c} <Icon name="chevron-right" size={11} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Nivel de confianza (R5.2) */}
        {!compact && (
          <div style={{ minWidth: 120, textAlign: "right" }}>
            <div className="text-faint" style={{ fontSize: 10.5, textTransform: "uppercase" }}>
              Confianza
            </div>
            <div className="kpi__value" style={{ fontSize: 22 }}>
              {confianzaPct}%
            </div>
            <div className="sla-bar" style={{ marginTop: 6 }}>
              <div
                className="sla-bar__fill"
                style={{ width: `${confianzaPct}%`, background: confianzaTone(confianza) }}
              />
            </div>
          </div>
        )}
      </div>

      {compact && (
        <div className="hstack" style={{ justifyContent: "space-between", marginTop: 8 }}>
          <span className="text-faint" style={{ fontSize: 10.5, textTransform: "uppercase" }}>
            Confianza
          </span>
          <strong className="text-mono" style={{ fontSize: 12 }}>{confianzaPct}%</strong>
        </div>
      )}
    </section>
  );
}
